// src/components/common/LikeButton.tsx
// Tombol interaktif "Dukung Vano" / Likes yang melekat per device dengan animasi mikro

import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { useSiteLikes } from '../../hooks/useSiteLikes';

interface LikeButtonProps {
  variant?: 'default' | 'compact' | 'floating';
  className?: string;
  showLabel?: boolean;
}

export const LikeButton: React.FC<LikeButtonProps> = ({
  variant = 'default',
  className = '',
  showLabel = true,
}) => {
  const { likesCount, isLiked, toggleLike, isLoading } = useSiteLikes();
  const [isBouncing, setIsBouncing] = useState(false);
  const [showHeartParticle, setShowHeartParticle] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 600);

    if (!isLiked) {
      setShowHeartParticle(true);
      setTimeout(() => setShowHeartParticle(false), 900);
    }

    toggleLike();
  };

  // 1. Variant Compact (Cocok di header atau navbar samping)
  if (variant === 'compact') {
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        title={isLiked ? 'Batal menyukai' : 'Beri like untuk Vano'}
        className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 cursor-pointer select-none active:scale-95 ${
          isLiked
            ? 'bg-rose-50 text-rose-600 border border-rose-200 shadow-xs'
            : 'bg-white text-gray-700 border border-gray-200 hover:border-rose-200 hover:text-rose-600 shadow-xs'
        } ${className}`}
      >
        <Heart
          className={`h-3.5 w-3.5 transition-transform duration-300 ${
            isLiked ? 'fill-rose-500 text-rose-500' : 'text-gray-400 group-hover:text-rose-500'
          } ${isBouncing ? 'scale-125' : 'scale-100'}`}
        />
        <span>{likesCount.toLocaleString()}</span>
      </button>
    );
  }

  // 2. Variant Default (Paling pas di Profile Header & Landing Hero)
  return (
    <div className={`relative inline-block ${className}`}>
      {/* Floating Mini Heart Particle on Like */}
      {showHeartParticle && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-rose-500 text-sm font-bold pointer-events-none animate-bounce flex items-center gap-0.5">
          <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
          <span>+1</span>
        </span>
      )}

      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`group relative inline-flex items-center gap-2.5 px-4 sm:px-5 py-2.5 rounded-2xl text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer select-none shadow-xs hover:shadow-md active:scale-95 border ${
          isLiked
            ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white border-rose-400 shadow-rose-200'
            : 'bg-white hover:bg-rose-50/50 text-gray-700 hover:text-rose-600 border-gray-200 hover:border-rose-300'
        }`}
      >
        <div
          className={`p-1 rounded-full transition-transform duration-300 ${
            isBouncing ? 'scale-130' : 'scale-100'
          }`}
        >
          <Heart
            className={`h-4 w-4 sm:h-5 sm:w-5 transition-colors ${
              isLiked ? 'fill-white text-white' : 'text-rose-500 group-hover:scale-110'
            }`}
          />
        </div>

        {showLabel && (
          <span className="font-medium tracking-tight">
            {isLiked ? 'Didukung' : 'Dukung Vano'}
          </span>
        )}

        {/* Badge Count */}
        <span
          className={`px-2 py-0.5 rounded-full text-[11px] font-bold transition-colors ${
            isLiked
              ? 'bg-white/25 text-white'
              : 'bg-rose-50 text-rose-600 group-hover:bg-rose-100'
          }`}
        >
          {likesCount.toLocaleString()}
        </span>
      </button>
    </div>
  );
};

export default LikeButton;
